import React, { forwardRef, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
    CategoryFilter,
    EnterpriseFilter,
    InclusionFilterPanel,
    PeopleIcon,
    DepartmentIcon,
    GlobeIcon,
    LevelIcon,
    TenureIcon,
    InclusionFilterPanelProps,
    filterCascaderSelectedValues,
    FeatherIcon,
    InclusionFilterPanelSkeleton,
} from '@diversioteam/diversio-ds'
import { CategoryState } from '@diversioteam/diversio-ds/dist/components/core/CategoryFilter/categoryFilter.types'
import {
    IntersectionalityFilterRef,
    Profile,
} from '@diversioteam/diversio-ds/dist/components/core/CascaderFilters/IntersectionalityFilter/intersectionalityFilter.types'
import { chartColors } from 'utils/styles/colors'
import {
    Block,
    Route,
} from '@diversioteam/diversio-ds/dist/components/core/FilterPanels/InlusionFilterPanel/components/DefaultView/defaultView.types'
import { parseYearQuarter } from 'utils'
import { createFiltersPayloadV2, _btoa_json_encode } from 'components/Analyze/Filters/utils'
import _ from 'underscore'
import { captureException, ErrorBoundary } from '@sentry/react'

import { withErrorBoundary } from 'config/withErrorBoundary/withErrorBoundary'
import { useBenchmarks } from 'hooks/benchmarks/useBenchmarks'
import { useGetCompanyFilters } from 'hooks/companyFilters/useGetCompanyFilters'
import { useGetCompanyProfiles } from 'hooks/companyProfiles/useGetCompanyProfiles'
import { useGetCompanyProfilesOptions } from 'hooks/companyProfiles/useGetCompanyProfilesOptions'
import { useCreateCompanyProfile } from 'hooks/companyProfiles/useCreateCompanyProfile'
import { useUpdateCompanyProfile } from 'hooks/companyProfiles/useUpdateCompanyProfile'
import { useDeleteCompanyProfile } from 'hooks/companyProfiles/useDeleteCompanyProfile'
import { AnalyzeView } from 'types/analyze.enum'
import { Queries } from 'api/queries.enum'
import { Survey } from 'api/actions/analyze/shared.types'
import {
    filterToPayloadKeyMapping,
    translatedFilters,
    translatedProfiles,
} from '../../../../utils/intersectionality-helpers'
import { useFilters } from 'hooks/useFilters'
import { useBreadcrumbs } from 'hooks/useBreadcrumbs'
import { generateCompanyURI } from 'sagas/helpers/generateCompanyURI'
import { useMessage } from 'hooks/useMessage'

import { IntersectionalityFilters } from './../../IntersectionalityFilters'
import { FilterPaneProps } from './filterPane.types'

import './index.scss'

const MAX_NUMBER_OF_PROFILES = 4

const tabToIconAndBlocksMapping = {
    target_groups: {
        icon: <PeopleIcon type="line" />,
        blocks: ['intersectionality', 'filters', 'resultsOverTime'],
    },
    department: {
        icon: <DepartmentIcon type="line" />,
        blocks: ['filters'],
    },
    office: {
        icon: <GlobeIcon type="line" />,
        blocks: ['filters'],
    },
    level: {
        icon: <LevelIcon type="line" />,
        blocks: ['filters'],
    },
    tenure: {
        icon: <TenureIcon type="line" />,
        blocks: ['filters'],
    },
}

const FilterPaneWithoutEB = forwardRef<IntersectionalityFilterRef, FilterPaneProps>(function FilterPane(
    { handleOnTabChange, selectedTab, selectedTabType }: FilterPaneProps,
    ref,
) {
    const queryClient = useQueryClient()
    const breadcrumbs = useBreadcrumbs()
    const { showMessage } = useMessage()

    const {
        setResultsOverTime,
        setSelectedFilters,
        setSelectedFiltersPayload,
        setSelectedSurvey,
        setShowFilters,
        setSelectedSurveysFilter,
        showFilters,
        selectedFiltersPayload,
        selectedFilters,
        selectedSurvey,
        profilesActive,
        setProfilesActive,
        defaultSelectedProfileId,
        resultsOverTime,
        handleToggleFiltersInProfilesActive,
        isFiltersInProfilesActive,
    } = useFilters({ view: AnalyzeView.Inclusion, selectedTab })

    const handleShowResultsOverTime = () => {
        setResultsOverTime?.(!resultsOverTime)
    }

    const [activeRouteKey, setActiveRouteKey] = useState<string | undefined>(selectedTab)

    const [survey, setSurvey] = useState<Survey | undefined>(undefined)
    const [surveyFilters, setSurveyFilters] = useState<string[]>([])
    const [routes, setRoutes] = useState<Route<'div'>[]>([])

    const { primaryIndustryBenchmark, primaryNationalBenchmark } = useBenchmarks()

    const params = useMemo(() => {
        return {
            ...selectedFiltersPayload,
            year: parseYearQuarter(selectedSurvey),
            key: selectedTab,
        }
    }, [selectedFiltersPayload, selectedSurvey, selectedTab])

    // Filter APIs only need year as a query param
    const filterParams = useMemo(() => {
        return {
            year: parseYearQuarter(selectedSurvey),
        }
    }, [selectedSurvey])

    const queryGetCompanyFilters = useGetCompanyFilters(filterParams)
    const queryGetCompanyProfiles = useGetCompanyProfiles(AnalyzeView.Inclusion, filterParams)
    const queryGetCompanyProfilesOptions = useGetCompanyProfilesOptions(AnalyzeView.Inclusion, filterParams)
    const mutationCreateCompanyProfile = useCreateCompanyProfile(AnalyzeView.Inclusion, params)
    const mutationUpdateCompanyProfile = useUpdateCompanyProfile(AnalyzeView.Inclusion, params)
    const mutationDeleteCompanyProfile = useDeleteCompanyProfile(AnalyzeView.Inclusion, params)

    useEffect(() => {
        if (selectedTab !== 'target_groups') {
            setResultsOverTime(false)
        }
    }, [selectedTab, setResultsOverTime])

    useEffect(() => {
        if (queryGetCompanyFilters.data?.surveys?.length) {
            const { name, year, quarter } = queryGetCompanyFilters.data.surveys[0]
            setSurvey(
                selectedSurvey || {
                    name,
                    year,
                    quarter,
                },
            )
        } else {
            setSurvey(undefined)
        }

        const inclusionRoutes = queryGetCompanyFilters.data?.filtersPane.inclusion.map((inclusionTab) => {
            const mapping = tabToIconAndBlocksMapping[inclusionTab.key as keyof typeof tabToIconAndBlocksMapping]
            return {
                key: inclusionTab.key,
                label: inclusionTab.name,
                icon: mapping?.icon || <FeatherIcon type="line" />,
                blocks: (mapping?.blocks || ['filters']) as Block[],
            }
        })

        if (inclusionRoutes) {
            setRoutes(inclusionRoutes)
        }
    }, [queryGetCompanyFilters.data])

    useEffect(() => {
        if (survey) {
            setSelectedSurvey(survey)
        }
    }, [survey, setSelectedSurvey])

    useEffect(() => {
        if (activeRouteKey) {
            if (!queryGetCompanyFilters.data?.filtersPane.inclusion) {
                return
            }

            const filter = _.findWhere(queryGetCompanyFilters.data?.filtersPane.inclusion || [], {
                uuid: activeRouteKey,
            })
            const validKey =
                _.findWhere(queryGetCompanyFilters.data?.filtersPane.inclusion || [], {
                    type: activeRouteKey,
                }) || filter

            const navigateTo = validKey ? activeRouteKey : 'target_groups'
            handleOnTabChange(navigateTo, filter?.type || '')
        }
    }, [handleOnTabChange, queryGetCompanyFilters.data?.filtersPane.inclusion, activeRouteKey])

    useEffect(() => {
        const filter =
            _.findWhere(queryGetCompanyFilters.data?.filtersPane.inclusion || [], {
                uuid: activeRouteKey,
            }) ||
            _.findWhere(queryGetCompanyFilters.data?.filtersPane.inclusion || [], {
                key: activeRouteKey,
            })

        let links = [
            {
                label: 'Inclusion',
                to: generateCompanyURI('/analyze2/inclusion'),
            },
            {
                label: filter?.name || '',
                to: generateCompanyURI(`/analyze2/inclusion/${activeRouteKey}/${location.search}`),
            },
        ]

        if (resultsOverTime) {
            links = [
                ...links,
                {
                    label: 'Results Over Time',
                    to: generateCompanyURI(
                        `/analyze2/inclusion/${activeRouteKey}/results-over-time/${location.search}`,
                    ),
                },
            ]
        }

        breadcrumbs?.setLinks(links)
    }, [activeRouteKey, queryGetCompanyFilters.data?.filtersPane.inclusion])

    useEffect(() => {
        const parsedSurveys = surveyFilters.map((survey) => {
            return survey.split(', ').reverse().join('')
        })

        return setSelectedSurveysFilter?.(_btoa_json_encode(parsedSurveys))
    }, [surveyFilters, setSelectedSurveysFilter])

    useEffect(() => {
        if (queryGetCompanyFilters.isLoading || !selectedFilters.length) {
            return
        }

        const ogFilters = queryGetCompanyFilters.data?.filtersModal
        const selectedFiltersPayload = createFiltersPayloadV2(selectedFilters, ogFilters)
        setSelectedFiltersPayload?.(selectedFiltersPayload)
    }, [selectedFilters, queryGetCompanyFilters.isLoading])

    useEffect(() => {
        queryClient.invalidateQueries({
            queryKey: [Queries.getInclusionData, params],
        })
    }, [primaryIndustryBenchmark, primaryNationalBenchmark, params, queryClient])

    const hasData = queryGetCompanyFilters.data && queryGetCompanyProfiles.data && queryGetCompanyProfilesOptions.data

    const handleChangeActiveRoute = (key: string) => {
        setActiveRouteKey(key)
    }

    const handleMultiSurveysApply = (options?: string[]) => {
        // Since the whitelabel options can have varied labels, this function makes
        // sure that the correct survey from the API is plucked based on the selected
        // option label and reparse the value to the correct format i.e. Q2, 2023 which
        // is then automatically handled by API middleware.
        const parsedOptions = options?.map((option) => {
            const selectedSurvey = queryGetCompanyFilters.data?.surveys.find(
                (surveyOption) => surveyOption.name === option,
            )

            return `Q${selectedSurvey?.quarter}, ${selectedSurvey?.year}`
        })

        setSurveyFilters(parsedOptions || [])
    }

    const handleToggleFilters = () => {
        setShowFilters?.(!showFilters)
    }

    const handleToggleIntersectionalityFilter = () => {
        setProfilesActive?.(!profilesActive)
    }

    const handleFilterDelete: InclusionFilterPanelProps['onDeleteFilter'] = (filter) => {
        setSelectedFilters?.(filterCascaderSelectedValues(selectedFilters || [], filter))
    }

    const handleSubmitProfiles = async (profiles: Profile[]) => {
        // Method that returns all promises for create requests
        // If a profile is already created, the request is changed to update
        const profilesCreateUpdateMutations = () => {
            return profiles.map(async (profile) => {
                const demographicsCategories = queryGetCompanyProfilesOptions.data?.filters.map(({ name }) => ({
                    name,
                    selected_options: null,
                }))

                const demographicsPayload = (demographicsCategories || []).map(({ name }) => {
                    const filter = profile.filters?.find(
                        (filter) =>
                            filterToPayloadKeyMapping[filter.name as keyof typeof filterToPayloadKeyMapping] === name,
                    )

                    if (filter) {
                        return {
                            name,
                            selected_options: filter.options,
                        }
                    }

                    return {
                        name,
                        selected_options: null,
                    }
                })

                const payload = {
                    label: profile.name,
                    demographics: demographicsPayload,
                }

                if (!profile.id) {
                    return await mutationCreateCompanyProfile.mutateAsync(payload)
                }

                return await mutationUpdateCompanyProfile.mutateAsync({
                    id: profile.id,
                    values: payload,
                })
            })
        }

        const deletedProfiles = queryGetCompanyProfiles.data?.profiles.flatMap(({ uuid }) => {
            if (profiles.find((profile) => profile.id === uuid)) {
                return []
            }

            return uuid
        })

        // Method that returns the list of promises to delete the profiles
        const profilesDeleteMutations = () => {
            return (
                deletedProfiles?.map(async (deletedProfile) => {
                    return await mutationDeleteCompanyProfile.mutateAsync(deletedProfile, {
                        onError: async (errors) => {
                            const response = await errors.json()
                            showMessage({ message: response.detail, type: 'error' })
                            captureException(errors)
                        },
                    })
                }) || []
            )
        }

        // This ensures that update/create happens before deleting profiles
        await Promise.all([...profilesCreateUpdateMutations()])
        await Promise.all([...profilesDeleteMutations()])

        setProfilesActive?.(false)

        await queryClient.invalidateQueries({
            queryKey: [Queries.getInclusionCompanyProfiles, filterParams],
        })

        await queryClient.invalidateQueries({
            queryKey: [Queries.getInclusionData, selectedTabType ? { ...params, type: selectedTabType } : {}],
        })
    }

    if (hasData) {
        // label and value needs to be same in order to show correct selected option inthe selectbox
        const surveyOptions = queryGetCompanyFilters.data.surveys.map(({ name }) => ({
            label: name,
            value: name,
        }))

        const filtersTypeIsComplex = queryGetCompanyFilters.data?.filtersModal.some(
            ({ filtersType }) => filtersType === 'complex',
        )

        const enableResultsOverTime = queryGetCompanyFilters.data.surveys.length > 1

        const categoryFilterData = queryGetCompanyFilters.data.filtersModal.map(({ name, options }) => ({
            name,
            options: Array.isArray(options) ? options : [],
        }))

        const profiles = queryGetCompanyProfiles.data.profiles.map(({ uuid, color, label, filters }) => ({
            id: uuid,
            color: chartColors[color - 1],
            name: label,
            filters: filters.flatMap(({ name, selectedOptions }) => ({
                name,
                options: selectedOptions || [],
            })),
        }))

        const hasMaxNumberOfProfiles = profiles.length === MAX_NUMBER_OF_PROFILES

        const nextProfilesColors = chartColors.flatMap((color, index) => {
            if (queryGetCompanyProfiles.data.profiles.find(({ color }) => color === index + 1)) {
                return []
            }

            return color
        })

        const getSurveyFilterLabels = (surveys: string[]): string[] => {
            return surveys.map(
                (survey) =>
                    queryGetCompanyFilters.data?.surveys.find(
                        (option) => `Q${option.quarter}, ${option.year}` === survey,
                    )?.name || '',
            )
        }

        const handleSurveysFiltersDelete = (filter: string) => {
            // Parse the value to correct format as surveyFilters to remove the deleted value
            const selectedSurvey = queryGetCompanyFilters.data?.surveys.find((option) => option.name === filter)

            if (selectedSurvey) {
                setSurveyFilters((prevState) =>
                    prevState.filter(
                        (prevFilter) => prevFilter !== `Q${selectedSurvey.quarter}, ${selectedSurvey.year}`,
                    ),
                )
            }
        }

        const handleSingleSurveyChange = (option: string | null) => {
            const label = surveyOptions.find((surveyOption) => surveyOption.value === option)?.label || ''
            const selectedSurvey = queryGetCompanyFilters.data?.surveys.find(({ name }) => name === label)

            if (!selectedSurvey) {
                return
            }

            const { year, quarter } = selectedSurvey

            if (option) {
                setSurvey({ year, quarter, name: option })
            }
        }

        return (
            <div className="ddsi-filter-panel">
                {!filtersTypeIsComplex && (
                    <ErrorBoundary>
                        <CategoryFilter
                            open={!!showFilters}
                            onSubmit={(data) => {
                                setSelectedFilters?.(data)
                                handleToggleFilters()
                            }}
                            onClose={handleToggleFilters}
                            data={categoryFilterData}
                            defaultSelected={selectedFilters as CategoryState}
                        />
                    </ErrorBoundary>
                )}

                {filtersTypeIsComplex && (
                    <ErrorBoundary>
                        <EnterpriseFilter
                            data={queryGetCompanyFilters.data.filtersModal}
                            open={!!showFilters}
                            onSubmit={(data) => {
                                setSelectedFilters?.(data)
                                handleToggleFilters()
                            }}
                            onClose={handleToggleFilters}
                            defaultSelected={selectedFilters || []}
                        />
                    </ErrorBoundary>
                )}

                <IntersectionalityFilters
                    open={profilesActive}
                    data={translatedFilters(queryGetCompanyProfilesOptions.data.filters)}
                    profiles={translatedProfiles(profiles)}
                    defaultActiveProfileId={defaultSelectedProfileId || profiles[0]?.id}
                    nextProfilesColors={nextProfilesColors}
                    onSubmit={handleSubmitProfiles}
                    onClose={handleToggleIntersectionalityFilter}
                    ref={ref}
                />

                <ErrorBoundary>
                    <InclusionFilterPanel
                        routes={routes}
                        activeRouteKey={activeRouteKey}
                        enableResultsOverTime={enableResultsOverTime}
                        singleSurveySelectProps={{
                            value: survey?.name,
                            options: surveyOptions,
                            onChange: handleSingleSurveyChange,
                        }}
                        multiSurveysSelectProps={{
                            values: getSurveyFilterLabels(surveyFilters),
                            options: surveyOptions,
                            disableAfterRootsSelected: 4,
                            onApply: handleMultiSurveysApply,
                        }}
                        profilesButtonType={hasMaxNumberOfProfiles ? 'edit' : 'create'}
                        onChangeActiveRoute={handleChangeActiveRoute}
                        onShowFilters={handleToggleFilters}
                        onShowResultsOverTime={handleShowResultsOverTime}
                        onCreateProfiles={handleToggleIntersectionalityFilter}
                        filters={selectedFilters || []}
                        onDeleteFilter={handleFilterDelete}
                        surveyFilters={getSurveyFilterLabels(surveyFilters)}
                        onSurveyFilterDelete={handleSurveysFiltersDelete}
                        isResultOverTimeActive={resultsOverTime}
                        isFiltersInProfilesActive={isFiltersInProfilesActive}
                        onToggleFiltersInProfilesActive={handleToggleFiltersInProfilesActive}
                    />
                </ErrorBoundary>
            </div>
        )
    }

    return <InclusionFilterPanelSkeleton />
})

export const FilterPane = withErrorBoundary<FilterPaneProps & { ref: React.Ref<IntersectionalityFilterRef> }>(
    FilterPaneWithoutEB,
    {},
)
